import { Link } from '@tanstack/react-router';
import { CircleUser } from 'lucide-react';
import type { ReactNode } from 'react';
import { t } from '../../i18n';
import { Icon } from '../icons';
import { useInsetVariable } from '../lib/use-inset-variable';
import { MoreMenu } from '../navigation/MoreMenu';
import { NavItem } from '../navigation/NavItem';
import { Wordmark } from '../navigation/Wordmark';
import { Button } from '../primitives/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../primitives/dropdown-menu';
import type { NavGroupDef, NavItemDef } from '../types';

type UserMenuProps = {
  name: string;
  roleLabel: string;
  items: Array<{ id: string; label: string; to?: string; onSelect?: () => void }>;
};

type AppShellProps = {
  /** The bottom bar on phones: at most five items, or four and Diğer when moreNav is set. */
  primaryNav: NavItemDef[];
  /** Behind Diğer on phones (merchant screens §1). */
  moreNav?: NavItemDef[];
  /** The sidebar from md; by default one untitled group with every item of primaryNav and moreNav. */
  sidebarNav?: NavGroupDef[];
  /** The route's staticData.nav (UI architecture §3). */
  activeNavId?: string;
  /** Where the Wordmark links. */
  homeTo: string;
  /** The environment badge, the admin's global search. */
  header?: ReactNode;
  notificationBell?: ReactNode;
  userMenu?: UserMenuProps;
  /** Between the header and main (merchant screens §4.3, §4.4). */
  banners?: ReactNode;
  children: ReactNode;
  /** Replace the catalog's texts for an app whose chrome is not Turkish. */
  labels?: { sidebarLabel?: string; bottomNavLabel?: string; userMenu?: string };
};

function UserMenu({ name, roleLabel, items, label }: UserMenuProps & { label: string }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={label}>
          <Icon icon={CircleUser} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="flex flex-col gap-1">
          <span>{name}</span>
          <span className="text-xs font-regular text-muted">{roleLabel}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map(({ id, label, to, onSelect }) => (
          <DropdownMenuItem key={id} asChild={to !== undefined} onSelect={onSelect}>
            {to === undefined ? label : <Link to={to}>{label}</Link>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// The frame of both panels and the console (design system §4, merchant screens §2). Both navs are always in the
// DOM and CSS switches them at md, so the bottom bar comes after main and each nav has its own name. Badge counts
// arrive in the items; the app feeds them from the stream.
export function AppShell({
  primaryNav,
  moreNav,
  sidebarNav,
  activeNavId,
  homeTo,
  header,
  notificationBell,
  userMenu,
  banners,
  children,
  labels,
}: AppShellProps) {
  // Toasts sit above the bottom bar (design system §4 Toast).
  const bottomNav = useInsetVariable<HTMLElement>('--nav-inset');
  const groups = sidebarNav ?? [{ id: 'all', items: [...primaryNav, ...(moreNav ?? [])] }];
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-(--z-sticky) flex flex-wrap items-center gap-2 border-b bg-surface px-4">
        {/* The environment badge sits next to the Wordmark (admin screens §1 rule 9); the bell and the user menu keep
            the far end. The group wraps at 360 px and 200 % text (design system §1). */}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <Link
            to={homeTo}
            className="inline-flex min-h-(--target-min) items-center rounded-sm focus-ring"
          >
            <Wordmark />
          </Link>
          {header}
        </div>
        {notificationBell}
        {userMenu && (
          <UserMenu {...userMenu} label={labels?.userMenu ?? t('components.AppShell.userMenu')} />
        )}
      </header>
      {banners}
      <div className="flex flex-1">
        <nav
          aria-label={labels?.sidebarLabel ?? t('components.AppShell.sidebarLabel')}
          className="hidden w-64 shrink-0 flex-col gap-4 border-e bg-surface p-4 md:flex"
        >
          {groups.map((group) => (
            <div key={group.id} className="flex flex-col gap-1">
              {group.label && (
                <h2 className="px-3 text-xs font-semibold text-muted">{group.label}</h2>
              )}
              <ul className="flex flex-col gap-1">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <NavItem item={item} variant="sidebar" active={item.id === activeNavId} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <main className="min-w-0 flex-1">{children}</main>
      </div>
      <nav
        ref={bottomNav}
        aria-label={labels?.bottomNavLabel ?? t('components.AppShell.bottomNavLabel')}
        className="sticky bottom-0 z-(--z-sticky) border-t bg-surface md:hidden"
      >
        <ul className="flex">
          {primaryNav.map((item) => (
            <li key={item.id} className="flex flex-1">
              <NavItem item={item} variant="bottom" active={item.id === activeNavId} />
            </li>
          ))}
          {moreNav && (
            <li className="flex flex-1">
              <MoreMenu items={moreNav} activeNavId={activeNavId} />
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
}
